import FirstPageIcon from '@mui/icons-material/FirstPage';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import LastPageIcon from '@mui/icons-material/LastPage';
import {
  Box,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow
} from '@mui/material';
import Paper from '@mui/material/Paper';
import { useTheme } from '@mui/material/styles';
import * as dates from 'date-arithmetic';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';
import { useState } from 'react';

function rangeFunc(start, end, unit = 'day') {
  let current = start;
  const days = [];
  while (dates.lte(current, end, unit)) {
    days.push(current);
    current = dates.add(current, 1, unit);
  }
  return days;
}

function inRange(e, start, end, accessors) {
  const eStart = dates.startOf(accessors.start(e), 'day');
  const eEnd = accessors.end(e);
  const startsBeforeEnd = dates.lte(eStart, end, 'day');
  const endsAfterStart = !dates.eq(eStart, eEnd, 'minutes')
    ? dates.gt(eEnd, start, 'minutes')
    : dates.gte(eEnd, start, 'minutes');
  return startsBeforeEnd && endsAfterStart;
}

const TablePaginationActions = props => {
  const theme = useTheme();
  const { count, page, rowsPerPage, onPageChange } = props;

  const handleFirstPageButtonClick = e => {
    onPageChange(e, 0);
  };

  const handleBackButtonClick = e => {
    onPageChange(e, page - 1);
  };

  const handleNextButtonClick = e => {
    onPageChange(e, page + 1);
  };

  const handleLastPageButtonClick = e => {
    onPageChange(e, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
  };

  return (
    <Box sx={{ flexShrink: 0, ml: 2.5 }}>
      <IconButton onClick={handleFirstPageButtonClick} disabled={page === 0} aria-label="first page">
        {theme.direction === 'rtl' ? <LastPageIcon /> : <FirstPageIcon />}
      </IconButton>
      <IconButton onClick={handleBackButtonClick} disabled={page === 0} aria-label="previous page">
        {theme.direction === 'rtl' ? <KeyboardArrowRight /> : <KeyboardArrowLeft />}
      </IconButton>
      <IconButton
        onClick={handleNextButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="next page"
      >
        {theme.direction === 'rtl' ? <KeyboardArrowLeft /> : <KeyboardArrowRight />}
      </IconButton>
      <IconButton
        onClick={handleLastPageButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="last page"
      >
        {theme.direction === 'rtl' ? <FirstPageIcon /> : <LastPageIcon />}
      </IconButton>
    </Box>
  );
};

TablePaginationActions.propTypes = {
  count: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  page: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.number.isRequired
};

export const AgendaView = ({ accessors, localizer, length, date, events }) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const renderDay = (day, events) => {
    events = events.filter(e => inRange(e, dates.startOf(day, 'day'), dates.endOf(day, 'day'), accessors));
    if (events.length > 0) {
      return (
        //     <div className=''>{accessors.title(event)}</div>
        <>
          <TableRow>
            <TableCell
              align="left"
              component="th"
              minwidth={100}
              scope="row"
              sx={{ verticalAlign: 'top', paddingTop: '16px' }}
              rowSpan={events.length + 1}
            >
              {localizer.format(day, 'ddd MMM DD')}
            </TableCell>
          </TableRow>
          {events?.map((event, idx) => (
            <TableRow
              key={idx}
              // sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
            >
              <TableCell className={events.length > 1 ? 'tableDeco' : ''} align="left">
                {dayjs(accessors.start(event)).format('h:mma')}
              </TableCell>
              <TableCell align="left">{dayjs(accessors.end(event)).format('h:mma')}</TableCell>
              <TableCell align="left">{event.subject_id ? event.subject_id : event.title}</TableCell>
              <TableCell align="left">{event.visit_type}</TableCell>
              <TableCell align="left">{event.assessment_type?.join(',')}</TableCell>
            </TableRow>
          ))}
        </>
      );
    }
  };

  // const timeRangeLabel = (day, event) => {
  //   const end = accessors.end(event)
  //   const start = accessors.start(event)

  //   if (!accessors.allDay(event)) {
  //     if (dayjs(start).day() === dayjs(end).day()) {
  //       const timePeriod = `${dayjs(start).format('h:mma')} – ${dayjs(
  //         end
  //       ).format('h:mma')}`
  //       return timePeriod
  //     } else {
  //       const startDate = dayjs(start).format('DD-MM YYYY, h:mma')
  //       const endDate = dayjs(end).format('DD-MM YYYY, h:mma')
  //       return `${startDate} – ${endDate}`
  //     }
  //   }
  // }

  /*Table Pagination - Start*/

  // Avoid a layout jump when reaching the last page with empty rows.
  // const emptyRows =
  //   page > 0 ? Math.max(0, (1 + page) * rowsPerPage - events.length) : 0;

  const handleChangePage = (e, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = e => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  /*Table Pagination - End */

  const end = dates.add(date, length, 'day');
  const range = rangeFunc(date, end, 'day');

  events = events.filter(e => Object.keys(e).includes('visit_type') && e.visit_type !== 'Interim Monitoring Visit');

  events = events.filter(event => inRange(event, date, end, accessors));
  events.sort((a, b) => +accessors.start(a) - +accessors.start(b));

  return (
    <div style={{height: '100vh', overflow: 'auto'}}>
      <Paper sx={{ width: '100%'}}>
        <TableContainer component={Paper} sx={{ maxHeight: 550 }}>
          <Table stickyHeader sx={{ minWidth: 650 }} aria-label="Agenda view" className="mainEvent">
            <TableHead>
              <TableRow>
                <TableCell align="left">Date</TableCell>
                <TableCell align="left" width={100}>
                  Time Start
                </TableCell>
                <TableCell align="left" width={100}>
                  Time End
                </TableCell>
                <TableCell align="left" minWidth={200}>
                  Subject ID
                </TableCell>
                <TableCell align="left" minWidth={200}>
                  Visit Type
                </TableCell>
                <TableCell align="left" minwidth={150}>
                  Assesment Type
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {events.length !== 0 ? (
                range.map((day, idx) => {
                  return (
                    <>
                      {renderDay(
                        day,
                        rowsPerPage > 0 ? events.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage) : events,
                        idx
                      )}
                    </>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={7}>
                    <Box
                      sx={{
                        minHeight: '230px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 'bold',
                        color: '#00000075',
                        fontSize: '15px'
                      }}
                    >
                      No event dates in range
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, { label: 'All', value: -1 }]}
          component="div"
          stickyFooter="true"
          count={events.length}
          rowsPerPage={rowsPerPage}
          page={page}
          SelectProps={{
            inputProps: {
              'aria-label': 'rows per page'
            },
            native: true
          }}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          ActionsComponent={TablePaginationActions}
        />
      </Paper>
    </div>
  );
};

AgendaView.title = (start, { localizer }) => {
  const end = dates.add(start, 1, 'month');
  return localizer.format({ start, end }, 'agendaHeaderFormat');
};

AgendaView.navigate = (date, action) => {
  const sDate = dayjs(date).startOf('month').toDate();
  switch (action) {
    case 'PREV':
      return dates.add(sDate, -1, 'month');
    case 'NEXT':
      return dates.add(sDate, 1, 'month');
    default:
      return date;
  }
};

AgendaView.propTypes = {
  events: PropTypes.array,
  date: PropTypes.instanceOf(Date),
  length: PropTypes.number,
  selected: PropTypes.object,
  accessors: PropTypes.object.isRequired,
  components: PropTypes.object.isRequired,
  getters: PropTypes.object.isRequired,
  localizer: PropTypes.object.isRequired
};
